<template>
  <div>
    <form v-if="!token" @submit="onEmailSubmit($event)" class="mx-auto my-5">
      <h3>Reset your password</h3>

      <p>Please enter your email address. We will send you an email to reset your password.</p>

      <div
        class="form-group"
        :class="{'has-error has-feedback': email && !valid, 'has-success has-feedback': email && valid}"
      >
        <input type="email" class="form-control" v-model="email" placeholder="Email address" />
        <span
          v-if="email"
          :class="{'glyphicon-ok': valid, 'glyphicon-remove': !valid}"
          class="glyphicon form-control-feedback"
        ></span>
        <span v-if="email && !valid" class="help-block">Email address is not valid.</span>
      </div>

      <button type="submit" class="btn btn-primary btn-lg mt-3" :disabled="disabled">
        <img v-if="loading" src="/wp-content/themes/snappy-theme/assets/images/loading2.gif" class="loading-image" />
        <span>Send Email</span>
      </button>
    </form>

    <password-change-form
      v-else
      ref="changeForm"
      class="mx-auto my-5"
      title="Please enter your new password"
      buttonText="Reset"
      :loading="loading"
      @save="onPasswordSave($event)"
    ></password-change-form>
  </div>
</template>

<script>
import axios from 'axios';
import swal from 'sweetalert2';
import PasswordChangeForm from '../components/auth/PasswordChangeForm';

export default {
  name: 'password-reset-page',
  components: {PasswordChangeForm},
  props: {
    token: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      email: '',
      loading: false,
    };
  },
  computed: {
    valid() {
      return /^\S+@\S+\.\S+$/.test(this.email);
    },
    disabled() {
      return !this.valid || this.loading;
    }
  },
  methods: {
    onEmailSubmit(event) {
      event.preventDefault();

      if (!this.disabled) {
        this.initiatePasswordReset();
      }
    },
    initiatePasswordReset() {
      this.loading = true;
      axios
        .post('/api/?entity=open&action=initiatePasswordReset', {
          email: this.email,
        })
        .then(result => {
          this.email = '';
          this.loading = false;
          this.showSuccessMessageForInitiatedAction();
        })
        .catch(error => {
          this.loading = false;
          const {status, data} = error.response;

          if (status === 422 && data && data.errors) {
            this.showErrorMessage(data.message, data.errors.email[0]);
          } else {
            this.showSuccessMessageForInitiatedAction();
          }
        });
    },
    onPasswordSave({password, passwordConfirmation}) {
      this.handlePasswordReset(password, passwordConfirmation);
    },
    handlePasswordReset(password, passwordConfirmation) {
      this.loading = true;

      axios
        .post('/api/?entity=open&action=handlePasswordReset', {
          password,
          password_confirmation: passwordConfirmation,
          token: this.token,
        })
        .then(result => {
          this.$refs.changeForm.resetData();
          this.loading = false;

          this.showSuccessMessage('Password successfully changed!')
            .then(result => {
              window.location.href = '/sk/login';
            });
        })
        .catch(error => {
          this.loading = false;
          const {status, data} = error.response;

          if (status === 422 && data && data.errors) {
            this.showErrorMessage(data.message, data.errors.password[0]);
          } else if (status === 401) {
            this.showErrorMessage('Invalid token', 'You have either entered a wrong URL or your password reset link has already expired.');
          } else {
            this.showErrorMessage('Password reset failed', 'Try again later or contact support.');
          }
        });
    },
    showErrorMessage(title, text) {
      return swal.fire({
        title,
        text,
        type: 'error',
        confirmButtonText: 'Ok',
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-lg btn-primary',
      });
    },
    showSuccessMessage(text) {
      return swal.fire({
        title: 'Success!',
        text,
        type: 'success',
      });
    },
    showSuccessMessageForInitiatedAction() {
      return swal.fire({
        title: 'Success!',
        text: 'If there is an account associated with that email address, we will send you a link to reset your password.',
        type: 'success',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
form {
  max-width: 400px;
}

.has-success .form-control-feedback {
  visibility: unset;
}

.loading-image {
  display: inline-block;
  margin-right: 5px;
  width: 20px;
}
</style>
